import { useMemo, useState } from "react";
import { Alert, Col, Container, Row } from "react-bootstrap";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import { MUNICIPAL_DEGRADABLE_INDICATOR_TYPES } from "@/customTypings/reference/MunicipalDegradable";
import { ConfirmationModel } from "../../components/features/common";
import { ResponsiveBreadcrumb } from "../../components/Layout/ResponsiveBreadcrumb";
import { ColumnRender, FilterType, IColumn, IFilter, IPagedListAction, SortOrder } from "../../components/UI/Declarations";
import { PagedList } from "../../components/UI/PagedList/PagedList";
import { municipalDegradablesService } from "../../services/reference/municipalDegradables.service";
import {
    appRoutes,
    buildRecordLifecycleConfirmProps,
    EntityLabels,
    formatReferenceSourceDisplay,
    ListPageTitles,
    recordLifecycleListConfirmBodies,
    referenceAdminEditPath,
    showToastSuccess,
} from "../../utils";
import {
    buildMdAdminPagedListActions,
    formatMdToastLabel,
    getMdAdminActionSuccessMessage,
    type MdAdminConfirmKind,
} from "./mdAdminListActions";

type ConfirmKind = MdAdminConfirmKind | null;

const MunicipalDegradablesListPage = () => {
    const navigate = useNavigate();

    const [refreshNumber, setRefreshNumber] = useState(0);
    const [actionError, setActionError] = useState<string | null>(null);
    const [confirmKind, setConfirmKind] = useState<ConfirmKind>(null);
    const [confirmMdId, setConfirmMdId] = useState<string | null>(null);
    const [confirmIndicatorType, setConfirmIndicatorType] = useState("");
    const [confirmApplies, setConfirmApplies] = useState("");

    const closeConfirm = () => {
        setConfirmKind(null);
        setConfirmMdId(null);
        setConfirmIndicatorType("");
        setConfirmApplies("");
    };

    const runConfirmedAction = () => {
        if (!confirmMdId || !confirmKind) return;
        const id = confirmMdId;
        const op =
            confirmKind === "disable"
                ? municipalDegradablesService.disable(id)
                : municipalDegradablesService.enable(id);
        op.then(() => {
            showToastSuccess(
                getMdAdminActionSuccessMessage(confirmKind, confirmIndicatorType, confirmApplies)
            );
            setActionError(null);
            setRefreshNumber((n) => n + 1);
            closeConfirm();
        }).catch((e: unknown) => {
            setActionError(typeof e === "string" ? e : "That action couldn't be completed. Please try again.");
            closeConfirm();
        });
    };

    const openConfirm = (
        kind: ConfirmKind,
        item: { id: string; indicatorType: string; applies: string }
    ) => {
        setConfirmKind(kind);
        setConfirmMdId(item.id);
        setConfirmIndicatorType(item.indicatorType);
        setConfirmApplies(item.applies);
    };

    const confirmLabel = formatMdToastLabel(confirmIndicatorType, confirmApplies);

    const confirmProps = useMemo(
        () =>
            buildRecordLifecycleConfirmProps(
                confirmKind,
                EntityLabels.municipalDegradableIndicator,
                confirmLabel,
                recordLifecycleListConfirmBodies(confirmLabel)
            ),
        [confirmKind, confirmLabel]
    );

    const indicatorTypeFilterOptions = useMemo(() => {
        const opts: Record<string, string | null> = { All: null };
        for (const t of MUNICIPAL_DEGRADABLE_INDICATOR_TYPES) opts[t] = t;
        return opts;
    }, []);

    const columns: IColumn[] = useMemo(
        () => [
            { member: "indicatorType", renderAs: ColumnRender.text, title: "Indicator", width: 120 } as IColumn,
            { member: "applies", renderAs: ColumnRender.text, title: "Applies", width: 90 } as IColumn,
            { member: "description", renderAs: ColumnRender.text, title: "Description", width: 320 } as IColumn,
            {
                member: "source",
                renderAs: ColumnRender.customRenderer,
                title: "Source",
                width: 120,
                customRenderer: (item: { source?: string }) => (
                    <td className="text-start">{formatReferenceSourceDisplay(item.source)}</td>
                ),
            } as IColumn,
            { member: "isActive", renderAs: ColumnRender.checkCross, title: "Active", width: 90, align: "center" } as IColumn,
        ],
        []
    );

    const filters: IFilter[] = useMemo(
        () => [
            {
                title: "Indicator",
                member: "indicatorType",
                type: FilterType.select,
                options: indicatorTypeFilterOptions,
            } as IFilter,
            { title: "Applies", member: "applies", type: FilterType.text } as IFilter,
            { title: "Description", member: "description", type: FilterType.text } as IFilter,
            { title: "Source", member: "source", type: FilterType.text } as IFilter,
            {
                title: "Active",
                member: "isActive",
                type: FilterType.select,
                options: { All: null, Yes: true, No: false },
            } as IFilter,
            {
                title: "Deleted",
                member: "isDeleted",
                type: FilterType.select,
                options: { All: null, Yes: true, No: false },
                defaultValue: false,
            } as IFilter,
        ],
        [indicatorTypeFilterOptions]
    );

    const itemActions: IPagedListAction[] = useMemo(
        () => [
            {
                text: "Edit",
                onClick: (item: { id: string }) => navigate(referenceAdminEditPath("municipalDegradables", item.id)),
                primary: true,
            } as IPagedListAction,
            ...buildMdAdminPagedListActions((kind, item) => openConfirm(kind, item)),
        ],
        [navigate]
    );

    return (
        <Container fluid>
            <Helmet>
                <title>{ListPageTitles.municipalDegradableIndicators}</title>
            </Helmet>
            <Row className="mb-3">
                <Col>
                    <ResponsiveBreadcrumb
                        items={[
                            { text: EntityLabels.referenceData, to: appRoutes.codes },
                            { text: ListPageTitles.municipalDegradableIndicators },
                        ]}
                    />
                    <h1>{ListPageTitles.municipalDegradableIndicators}</h1>
                    <p className="text-muted">
                        Fixed regulatory indicators for municipal and degradable waste reporting.
                        You can edit the display description and turn entries on or off in lists.
                    </p>
                    {actionError && (
                        <Alert variant="danger" className="mt-2" onClose={() => setActionError(null)} dismissible>
                            {actionError}
                        </Alert>
                    )}
                </Col>
            </Row>
            <Row>
                <Col>
                    <PagedList
                        columns={columns}
                        filters={filters}
                        itemActions={itemActions}
                        rowActionsAriaLabel="Indicator actions"
                        defaultSortBy="Applies"
                        defaultSortOrder={SortOrder.ascending}
                        getUrl="/api/referencedata/municipal-degradables/all"
                        rowFormatter={() => null}
                        persistanceKey="MunicipalDegradablesListPage"
                        refreshNumber={refreshNumber}
                    />
                </Col>
            </Row>

            <ConfirmationModel
                show={confirmKind !== null}
                onClose={(confirmed) => {
                    if (confirmed) runConfirmedAction();
                    else closeConfirm();
                }}
                title={confirmProps.title}
                body={confirmProps.body}
                buttonSuccessVariant={confirmProps.buttonSuccessVariant}
                buttonSuccessText={confirmProps.buttonSuccessText}
                buttonCancelText="Cancel"
            />
        </Container>
    );
};

export { MunicipalDegradablesListPage };
